import { createContext, useContext } from 'react';
import { ACCESS_TOKEN_LOCAL_STORAGE_KEY, AUTH_USER_LOCAL_STORAGE_KEY } from 'constant';
import { AccessToken, AxiosResponseType } from 'types';

// In the file where AuthContextProps is defined
export type AuthContextProps = {
  user: AxiosResponseType;
  isUserLoading: boolean;
  // True when the user authenticated (Google SSO) but is not authorised to use the data tool.
  accessDenied: boolean;
  // Email of the signed-in-but-unauthorised user, for the access-denied message.
  deniedEmail: string;
  setUser: (values: AxiosResponseType) => void;
  resendCode: (username: string) => Promise<void>;
  verifyCode: (username: string, code: string) => Promise<void>;
  verifyMFA: (code: string, mfaType: 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA' | null | undefined) => Promise<void>;
  passwordRecovery: (email: string) => Promise<void>;
  passwordReset: (email: string, code: string, newPassword: string) => Promise<void>; // Add this line
};

export const setUserToLocalStorage = (user: AxiosResponseType) => {
  const idToken = user?.signInUserSession?.idToken;
  const jwtToken =
    (typeof idToken?.getJwtToken === 'function' ? idToken.getJwtToken() : idToken?.jwtToken) || '';
  const expiresAtMs = idToken?.payload?.exp ? Number(idToken.payload.exp) * 1000 : Date.now();

  const tokenData: AccessToken = {
    timeout: Math.max(expiresAtMs - Date.now(), 0),
    id: user.username,
    isExpire: false,
    createdOn: new Date().getTime(),
    token: jwtToken,
    user_name: user?.username || '',
    user_email: user?.challengeParam?.userAttributes?.email || user?.attributes?.email || '',
  };

  localStorage.setItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY, JSON.stringify(tokenData));
  // loadIntercom();
  Promise.resolve({
    email: user?.attributes?.email || '',
    name: user?.attributes?.name || '',
    created_at: new Date().getTime() / 1000,
  }).then(() => {
    // bootIntercom(user);
  });
};

export const setAuthUserToLocalStorage = (user: AxiosResponseType) => {
  localStorage.setItem(AUTH_USER_LOCAL_STORAGE_KEY, JSON.stringify(user || {}));
};

export const checkAccessTokenExpiration = () => {
  const tokenLocalStorageData = localStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
  const tokenData = tokenLocalStorageData ? JSON.parse(tokenLocalStorageData) : null;

  if (tokenData) {
    const tokenTimeOutData = Number(tokenData.timeout || 0);
    const token =
      typeof tokenData.token === 'string' ? tokenData.token : tokenData?.token?.jwtToken || false;
    const timeOutData = Date.now() - Number(tokenData.createdOn || 0);

    if (!token || !tokenTimeOutData || timeOutData > tokenTimeOutData) {
      return false;
    }

    return token;
  }

  return false;
};

// In the file where AuthContext is created
export const AuthContext = createContext<AuthContextProps>({
  user: {} as AxiosResponseType,
  isUserLoading: false,
  accessDenied: false,
  deniedEmail: '',
  setUser: () => null,
  resendCode: () => Promise.resolve(),
  verifyCode: () => Promise.resolve(), // Add a default implementation here
  verifyMFA: () => Promise.resolve(), // Add a default implementation here
  passwordRecovery: () => Promise.resolve(), // Add a default implementation here
  passwordReset: () => Promise.resolve(), // Add a default implementation here
});

export const useAuth = (): Readonly<AuthContextProps> => useContext(AuthContext);
